/**
 * Capability 3: Trust Workflows — Altara's orchestration engine.
 *
 * This is intentionally NOT a nav folder. It's a workflow control surface:
 *   - Create workflow CTA
 *   - Template catalogue
 *   - Running workflow lanes (Active · Waiting · Completed · Failed)
 *   - Workflow Designer (future)
 *
 * Every regulated interaction starts here.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CapabilityLandingPage, { NAVY, GOLD } from "@/components/capability/CapabilityLandingPage";
import api from "@/lib/api";
import {
  Plus, PlayCircle, PauseCircle, CheckCircle2, XCircle, Puzzle, Workflow,
  KeyRound, Fingerprint, ShieldCheck, GraduationCap, BookOpen, ClipboardCheck,
  FileCheck2, Dice5, Wand2,
} from "lucide-react";

const TEMPLATE_CATALOGUE = [
  { id: "customer-onboarding",    name: "Customer Onboarding",     icon: KeyRound,      steps: 5, category: "Identity + Consent" },
  { id: "investor-onboarding",    name: "Investor Onboarding",     icon: FileCheck2,    steps: 6, category: "Identity + Consent + Affordability" },
  { id: "gaming-registration",    name: "Gaming Registration",     icon: Dice5,         steps: 5, category: "Identity + RG Threshold" },
  { id: "product-understanding",  name: "Product Understanding",   icon: BookOpen,      steps: 4, category: "Learning + Assessment" },
  { id: "identity-verification",  name: "Identity Verification",   icon: Fingerprint,   steps: 3, category: "Identity" },
  { id: "kyc-verification",       name: "KYC Verification",        icon: ShieldCheck,   steps: 4, category: "Identity + Sanctions + PEP" },
  { id: "learning-assignment",    name: "Learning Assignment",     icon: GraduationCap, steps: 3, category: "Learning" },
];

const LANE_META = {
  active:    { label: "Active",    icon: PlayCircle,  color: "#0F766E", bg: "#CCFBF1" },
  waiting:   { label: "Waiting",   icon: PauseCircle, color: "#92400E", bg: "#FEF3C7" },
  completed: { label: "Completed", icon: CheckCircle2,color: "#065F46", bg: "#D1FAE5" },
  failed:    { label: "Failed",    icon: XCircle,     color: "#7F1D1D", bg: "#FEE2E2" },
};

export default function TrustWorkflowsCapability() {
  const [kpis, setKpis] = useState({ consent: null, kyc: null, certs: null, running: null });

  useEffect(() => {
    (async () => {
      const next = { consent: null, kyc: null, certs: null, running: null };
      try { const { data } = await api.get("/trust-requests/kpis"); next.running = data?.open ?? null; } catch { /* endpoint optional */ }
      try { const { data } = await api.get("/consent/requests"); next.consent = Array.isArray(data) ? data.filter((r) => r.status === "granted").length : null; } catch { /* endpoint optional */ }
      // KYC + certs counters — leave honest placeholders unless endpoint exists
      try { const { data } = await api.get("/trust/certificates/count"); next.certs = data?.count ?? null; } catch { /* endpoint optional */ }
      setKpis(next);
    })();
  }, []);

  return (
    <CapabilityLandingPage
      eyebrow="Capability 3 · Trust Workflows"
      title="Trust Workflows"
      accentColor={GOLD}
      purpose="Reusable trust journeys that orchestrate verification, consent, learning and evidence generation. Not pages — reusable workflow components. Every regulated interaction starts here."
      primaryActions={[
        { label: "Create Workflow", to: "/workflows/templates", icon: Plus },
        { label: "Send a Request", to: "/requests", icon: Wand2 },
      ]}
      kpis={[
        { label: "Consent grants", value: kpis.consent, subLabel: "granted, active" },
        { label: "KYC verifications", value: null, emptyHint: "Live once first vendor wired" },
        { label: "Certificates issued", value: kpis.certs, subLabel: "Ed25519 signed" },
        { label: "Running workflows", value: kpis.running, subLabel: "in flight" },
      ]}
    >
      {/* Template catalogue ─────────────────────────────── */}
      <section>
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
            Available templates
          </div>
          <Link to="/workflows/templates" className="text-[12px] font-semibold text-slate-500 hover:text-slate-800" data-testid="view-all-templates">
            View all →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="workflow-templates-catalogue">
          {TEMPLATE_CATALOGUE.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.id}
                to={`/workflows/templates/${t.id}`}
                data-testid={`tpl-${t.id}`}
                className="group rounded-2xl bg-white border border-slate-100 p-4 hover:border-slate-300 hover:shadow-sm transition"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${GOLD}22`, color: GOLD }}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[14px]" style={{ color: NAVY }}>{t.name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{t.category}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-2">{t.steps} steps</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Running workflow lanes ─────────────────────────── */}
      <section>
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
          Running workflows
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="workflow-lanes">
          {Object.entries(LANE_META).map(([key, meta]) => {
            const Icon = meta.icon;
            return (
              <div
                key={key}
                data-testid={`lane-${key}`}
                className="rounded-2xl bg-white border border-slate-100 p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ background: meta.bg, color: meta.color }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: meta.color }}>
                    {meta.label}
                  </span>
                </div>
                <div className="font-display text-2xl font-semibold text-slate-400">—</div>
                <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-1">
                  Engine ships Phase 1.5
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Workflow Designer teaser ────────────────────────── */}
      <section
        data-testid="workflow-designer-teaser"
        className="rounded-2xl border-2 border-dashed border-slate-200 p-6 flex items-start gap-4"
      >
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#F1F5F9", color: NAVY }}>
          <Puzzle className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="font-display font-semibold text-lg" style={{ color: NAVY }}>Workflow Designer</div>
            <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded" style={{ background: "#F1F5F9", color: "#475569" }}>
              Future
            </span>
          </div>
          <p className="text-[13px] text-slate-600 mt-1 max-w-2xl">
            Drag-and-drop BPM canvas to compose new workflows from primitives (Consent, Identity, KYC, Learning, Affordability, Product Understanding). Publish, version, and A/B test workflow variants per tenant.
          </p>
        </div>
      </section>

      {/* Sub-capabilities: workflow primitives ──────────── */}
      <section>
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
          Workflow primitives
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <Primitive to="/consent-ledger"      icon={KeyRound}      name="Consent"                  status="Live" />
          <Primitive to="/workflows/identity"  icon={Fingerprint}   name="Identity Verification"    status="Live" />
          <Primitive to="/kyc-orchestration"   icon={ShieldCheck}   name="KYC"                      status="Live" />
          <Primitive to="/workflows/affordability" icon={ClipboardCheck} name="Affordability"       status="Phase 1" />
          <Primitive to="/workflows/product-understanding" icon={BookOpen} name="Product Understanding" status="Phase 1" />
          <Primitive to="/workflows/learning-requests" icon={GraduationCap} name="Learning Requests" status="Phase 1" />
          <Primitive to="/trust-certificates"  icon={FileCheck2}    name="Trust Certificates"       status="Live" />
          <Primitive to="/workflows/templates" icon={Workflow}      name="Workflow Templates"       status="Live" />
        </div>
      </section>
    </CapabilityLandingPage>
  );
}

function Primitive({ to, icon: Icon, name, status }) {
  const tone =
    status === "Live" ? { bg: "#D1FAE5", color: "#065F46" } :
    status === "Phase 1" ? { bg: "#FEF3C7", color: "#92400E" } :
    { bg: "#F1F5F9", color: "#475569" };
  return (
    <Link
      to={to}
      data-testid={`primitive-${name.toLowerCase().replace(/\s+/g, "-")}`}
      className="group rounded-2xl bg-white border border-slate-100 p-4 flex items-center gap-3 hover:border-slate-300 hover:shadow-sm transition"
    >
      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#F1F5F9", color: NAVY }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[14px]" style={{ color: NAVY }}>{name}</div>
      </div>
      <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded shrink-0"
        style={{ background: tone.bg, color: tone.color }}>
        {status}
      </span>
    </Link>
  );
}
